import React, { useState, useEffect } from "react";
import styled from "@xstyled/styled-components";
import { tokens } from "../../utils/theme";
import { Panel, PanelHeader, PanelTitle } from "../../components/SharedPageStyles";
import { FiChevronUp, FiChevronDown } from "react-icons/fi";
import { useJournalData } from "../../context/JournalContext";
// import { BROKER_RULES } from "../../utils/brokerRules"; // Removed
import { useTheme } from "@mui/material";
import { API_URL } from "../../config";

const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  max-width: 1000px; /* Slightly reduced for better readability on large screens? Or keep 1200. User said enlarge. Let's keep 1200 or make 100% */
  width: 100%;
  margin: 0 auto;
  justify-content: center; /* helps if we add min-height */
`;

const SectionBlock = styled.div`
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  border-radius: 12px;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)'};
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
  padding-bottom: 12px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.span`
  font-size: 12px;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Value = styled.span`
  font-size: 16px;
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
  font-weight: 500;
  min-height: 24px;
`;

const BalanceValue = styled(Value)`
  font-size: 24px;
  font-weight: 700;
  color: ${props => tokens(props.theme.palette.mode).greenAccent[500]};
`;

const StyledInput = styled.input`
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => tokens(props.theme.palette.mode).grey[600]};
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  width: 100%;
  
  &:focus {
    outline: none;
    border-color: ${props => tokens(props.theme.palette.mode).blueAccent[500]};
    box-shadow: 0 0 0 2px rgba(68, 138, 255, 0.1);
  }

  /* Hide native spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const SpinnerWrapper = styled.div`
  position: absolute;
  right: 2px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 0px;
  opacity: 0;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 1;
  }
`;

const SpinnerBtn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 10px;
  cursor: pointer;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
  transition: all 0.1s;
  
  &:hover {
    color: ${props => tokens(props.theme.palette.mode).blueAccent[400]};
    background: rgba(255, 255, 255, 0.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const NumberInputWrapper = styled.div`
  position: relative;
  width: 100%;

  &:hover ${SpinnerWrapper} {
    opacity: 1;
  }
`;

const CustomNumberInput = ({ value, onChange, placeholder, step = 1, ...props }) => {
    const handleIncrement = (e) => {
        e.preventDefault();
        const current = parseFloat(value) || 0;
        const precision = (step.toString().split('.')[1] || '').length;
        onChange((current + step).toFixed(precision));
    };

    const handleDecrement = (e) => {
        e.preventDefault();
        const current = parseFloat(value) || 0;
        const precision = (step.toString().split('.')[1] || '').length;
        onChange((current - step).toFixed(precision));
    };

    return (
        <NumberInputWrapper>
            <StyledInput
                type="number"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                style={{ paddingRight: '20px' }}
                {...props}
            />
            <SpinnerWrapper>
                <SpinnerBtn onClick={handleIncrement}>
                    <FiChevronUp size={10} />
                </SpinnerBtn>
                <SpinnerBtn onClick={handleDecrement}>
                    <FiChevronDown size={10} />
                </SpinnerBtn>
            </SpinnerWrapper>
        </NumberInputWrapper>
    );
};

const StyledSelect = styled.select`
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => tokens(props.theme.palette.mode).grey[600]};
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  width: 100%;
  
  &:focus {
    outline: none;
    border-color: ${props => tokens(props.theme.palette.mode).blueAccent[500]};
    box-shadow: 0 0 0 2px rgba(68, 138, 255, 0.1);
  }
  
  option {
    background: ${props => props.theme.palette.mode === 'dark' ? '#1e2130' : '#ffffff'};
  }
`;

const Settings = React.forwardRef(({ isEmbedded = false, isEditing = false, profileData = null, onSave = null }, ref) => {
    const { userProfile, updateUserProfile } = useJournalData();
    const theme = useTheme();

    // Determine initial state source
    const initialSource = profileData || userProfile;

    // Use local state for editing
    const [profile, setProfile] = useState(initialSource);

    // Sync with source when not editing, or when source changes (and we aren't editing)
    useEffect(() => {
        if (!isEditing) {
            setProfile(profileData || userProfile);
        }
    }, [userProfile, profileData, isEditing]);

    // Temporary state for editing balance
    const [editedBalance, setEditedBalance] = useState("");

    // Initialize editedBalance when entering edit mode
    useEffect(() => {
        if (isEditing) {
            setEditedBalance((profile.initialBalance || profile.initial_balance)?.toString() || "");
        }
    }, [isEditing, profile.initialBalance, profile.initial_balance]);

    React.useImperativeHandle(ref, () => ({
        triggerSave: () => {
            const newInitial = parseFloat(editedBalance);

            let updatedProfile = { ...profile };
            if (!isNaN(newInitial)) {
                updatedProfile.initial_balance = newInitial;
            }

            if (!updatedProfile.creation_date && !updatedProfile.creationDate) {
                updatedProfile.creation_date = new Date().toLocaleDateString('en-US');
            }

            // Validation to ensure no required field is null or empty
            if (!updatedProfile.name || updatedProfile.name.trim() === "") {
                alert("Account Name is required.");
                return;
            }
            if (!updatedProfile.broker || updatedProfile.broker.trim() === "") {
                alert("Please select a Broker or Firm. This field is mandatory.");
                return;
            }
            if (!updatedProfile.currency) {
                alert("Please select a Currency. This field is mandatory.");
                return;
            }

            if (onSave) {
                onSave(updatedProfile);
            } else {
                updateUserProfile(updatedProfile);
            }
        },
        cancel: () => {
            setProfile(profileData || userProfile);
        }
    }));

    // Fetch Broker Rules
    const [fetchedRules, setFetchedRules] = useState(null);
    useEffect(() => {
        fetch(`${API_URL}/config/broker-rules`)
            .then(res => res.json())
            .then(data => setFetchedRules(data))
            .catch(err => console.error("Failed to fetch broker rules:", err));
    }, []);

    const handleChange = (field, value) => {
        setProfile(prev => {
            const newState = { ...prev, [field]: value };

            // Removed auto-select for deprecated fields
            return newState;
        });
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(val);
    };

    const content = (
        <SettingsContainer>
            <SectionBlock>
                <SectionHeader>
                    <SectionTitle>Account Information</SectionTitle>
                </SectionHeader>

                <InfoGrid>
                    <InfoItem>
                        <Label>Account Name</Label>
                        {isEditing ? (
                            <StyledInput
                                value={profile.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                            />
                        ) : (
                            <Value>{profile.name}</Value>
                        )}
                    </InfoItem>

                    <InfoItem>
                        <Label>Creation Date</Label>
                        {isEditing ? (
                            <StyledInput
                                type="date"
                                value={
                                    profile.creation_date || profile.creationDate
                                        ? (() => {
                                            try {
                                                const d = new Date(profile.creation_date || profile.creationDate);
                                                return !isNaN(d.getTime()) ? d.toISOString().split('T')[0] : '';
                                            } catch (e) {
                                                return '';
                                            }
                                        })()
                                        : new Date().toISOString().split('T')[0]
                                }
                                onChange={(e) => {
                                    if (e.target.value) {
                                        const [y, m, d] = e.target.value.split('-');
                                        const formattedDate = new Date(y, m - 1, d).toLocaleDateString('en-US');
                                        handleChange('creation_date', formattedDate);
                                    }
                                }}
                            />
                        ) : (
                            <Value>{profile.creation_date || profile.creationDate || new Date().toLocaleDateString('en-US')}</Value>
                        )}
                    </InfoItem>

                    <InfoItem>
                        <Label>Account Type</Label>
                        {isEditing ? (
                            <StyledSelect
                                value={profile.account_type || 'Firm'}
                                onChange={(e) => handleChange('account_type', e.target.value)}
                            >
                                <option value="Firm">Firm</option>
                                <option value="Personal">Personal</option>
                            </StyledSelect>
                        ) : (
                            <Value>{profile.account_type || 'Firm'}</Value>
                        )}
                    </InfoItem>

                    <InfoItem>
                        <Label>Broker / Firm</Label>
                        {isEditing ? (
                            <StyledSelect
                                value={profile.broker || ''}
                                onChange={(e) => handleChange('broker', e.target.value)}
                            >
                                <option value="" disabled>Select Broker / Firm</option>
                                {fetchedRules && Object.keys(fetchedRules).length > 0 ? (
                                    Object.keys(fetchedRules).map(brokerName => (
                                        <option key={brokerName} value={brokerName}>{brokerName}</option>
                                    ))
                                ) : (
                                    <>
                                        <option value="FTMO">FTMO</option>
                                        <option value="Lucid">Lucid</option>
                                        <option value="Lucid Micro">Lucid Micro</option>
                                        <option value="Pepperstone">Pepperstone</option>
                                        <option value="Interactive Brokers">Interactive Brokers</option>
                                        <option value="Trade Republic">Trade Republic</option>
                                    </>
                                )}
                            </StyledSelect>
                        ) : (
                            <Value>{profile.broker || 'N/A'}</Value>
                        )}
                        {/* Display Broker/Firm Description/Type */}
                        {(() => {
                            if (!fetchedRules || !profile.broker) return null;
                            const activeName = profile.broker;
                            // Try exact match or first word
                            const rule = fetchedRules[activeName] || fetchedRules[activeName.split(' ')?.[0]];
                            if (rule?.description) {
                                return (
                                    <div style={{ fontSize: '11px', color: tokens(theme.palette.mode).grey[400], marginTop: '4px' }}>
                                        {rule.description}
                                    </div>
                                );
                            }
                            return null;
                        })()}

                    </InfoItem>

                    {(() => {
                        const activeName = profile.broker;
                        const rule = fetchedRules && activeName ? (fetchedRules[activeName] || fetchedRules[activeName.split(' ')?.[0]]) : null;
                        const instrument = rule ? (rule.defaultInstrumentType || 'N/A') : (profile.instrument_type || 'N/A');
                        const platform = rule ? (rule.platform || 'N/A') : (profile.platform || 'N/A');

                        return (
                            <>
                                <InfoItem>
                                    <Label>Instrument Type</Label>
                                    <Value>{instrument}</Value>
                                </InfoItem>

                                <InfoItem>
                                    <Label>Platform</Label>
                                    <Value>{platform}</Value>
                                </InfoItem>
                            </>
                        );
                    })()}

                    <InfoItem>
                        <Label>Currency</Label>
                        {isEditing ? (
                            <StyledSelect
                                value={profile.currency || ''}
                                onChange={(e) => handleChange('currency', e.target.value)}
                            >
                                <option value="" disabled>Select Currency</option>
                                <option value="USD ($)">USD ($)</option>
                                <option value="EUR (€)">EUR (€)</option>
                            </StyledSelect>
                        ) : (
                            <Value>{profile.currency}</Value>
                        )}
                    </InfoItem>
                    <InfoItem>
                        <Label>Deposits</Label>
                        {isEditing ? (
                            <CustomNumberInput
                                value={editedBalance}
                                onChange={setEditedBalance}
                                step={100}
                            />
                        ) : (
                            <BalanceValue>{formatCurrency(profile.initialBalance || profile.initial_balance || profile.balance)}</BalanceValue>
                        )}
                    </InfoItem>
                </InfoGrid>
            </SectionBlock>
        </SettingsContainer >
    );

    if (isEmbedded) {
        return content;
    }

    return (
        <Panel>
            <PanelHeader>
                <PanelTitle>Settings</PanelTitle>
            </PanelHeader>
            {content}
        </Panel>
    );
});

export default Settings;
